import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightLeft, ChevronRight, Plane } from 'lucide-react';
import { FlightBooking } from '@/api/entities';
import { getCityName } from '@/components/airportUtils';
import { TRIPS_LIST_ITEM_SHELL } from '@/constants/brandColors';
import { useTripsFlightStatus } from '@/hooks/useFlightLive';
import { cn } from '@/lib/utils';
import PredictionsTag from './PredictionsTag';
import { isNativeApp } from '@/native/bootstrap';
import { isOffline, loadOfflineTrips, offlineAccount } from '@/native/offline';
import { resolveCarrierLogo } from '@/utils/airlineBranding';
import {
  STATUS_TONE_CLASSES,
  connectionLine,
  flightEnd,
  forecastLine,
  gateLine,
  liveCountdown,
  nextLineParts,
  statusHeadline,
  updatedAgo,
} from '@/utils/flightStatusDisplay';
import { departsIn, departureDayLabel, flightProgress, isFlightHome, localClock, nextFlight, pickTripNow } from '@/utils/tripNow';

/**
 * The trip a signed-in customer's Home page shows under its hero, the way
 * airline apps lead with your next flight: about to leave, under way, or just landed
 * (src/utils/tripNow.js picks it). Live status takes over from the booked
 * times once the flight has it, and a tap opens the trip.
 *
 * Loads the viewer's own bookings (FlightBooking.listMyOwn), never a page's
 * list, which for an admin is everyone's. Renders nothing until there is
 * such a trip, so the page never shifts for anyone else. A flight without live status just shows its booked times,
 * with no reason given (ticket status stays discretionary: founder hard rule).
 */
export default function TripNowCard({ className }) {
  const [bookings, setBookings] = useState(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // The iOS app without signal: the trips saved on the phone (src/native/offline.js).
      if (isNativeApp && isOffline()) {
        const account = offlineAccount();
        const saved = account ? loadOfflineTrips(account.id) : null;
        if (!cancelled && saved) setBookings(saved.bookings);
        return;
      }
      const mine = await FlightBooking.listMyOwn();
      if (!cancelled) setBookings(mine);
    })().catch(() => {
      // The page works without it.
    });
    return () => { cancelled = true; };
  }, []);

  // The countdown and the trip's phase move with the clock.
  useEffect(() => {
    const timer = setInterval(() => setNowMs(Date.now()), 60000);
    return () => clearInterval(timer);
  }, []);

  const tripNow = useMemo(() => (bookings ? pickTripNow(bookings, nowMs) : null), [bookings, nowMs]);
  const statusTrips = useMemo(() => (tripNow ? [tripNow.bookings] : []), [tripNow]);
  const statusByBooking = useTripsFlightStatus(statusTrips);

  if (!tripNow) return null;

  const liveOf = (flight) => (statusByBooking[flight.bookingId] || [])
    .find((s) => s.slice_index === flight.sliceIndex && s.segment_index === flight.segmentIndex) || null;
  const next = nextFlight(tripNow.flights, nowMs, (f) => liveOf(f)?.status || null);
  const live = liveOf(next);
  const status = live?.status || null;
  const booking = tripNow.bookings.find((b) => b.id === next.bookingId) || tripNow.bookings[0];
  const reference = booking?.bookingReference || booking?.booking_reference || booking?.tripReference || null;

  const headline = statusHeadline(status);
  const dep = status ? flightEnd(status, 'departure') : null;
  const arr = status ? flightEnd(status, 'arrival') : null;
  const flightLabel = live?.flight || next.label;
  const day = departureDayLabel(next, nowMs);
  const countdown = !status ? departsIn(next, nowMs) : null;
  // On the day, from the airline's live time: "Leaves in 2h 05m", "Lands in 48m".
  const tick = liveCountdown(status, nowMs);
  // The early warnings, in the same words and numbers as the emails.
  const forecast = forecastLine(live?.forecast, status, getCityName);
  const connection = connectionLine(live?.connection, getCityName);
  const connectionWarning = Boolean(live?.connection?.level);
  const progress = flightProgress(status, nowMs);
  const inTheAir = status?.state === 'departed';
  const landed = ['landed', 'arrived'].includes(status?.state);
  const logo = resolveCarrierLogo(next.carrier);

  let eyebrow;
  if (inTheAir) eyebrow = 'In the air';
  else if (landed) eyebrow = 'Landed';
  else if (tripNow.phase === 'upcoming') eyebrow = 'Upcoming trip';
  else if (tripNow.phase === 'traveling') eyebrow = isFlightHome(next, tripNow.flights) ? 'Flight home' : 'Next flight';
  else eyebrow = 'Your trip';

  const departure = {
    code: next.origin,
    verb: dep?.verb || 'Departs',
    time: dep?.time || localClock(next.departingAtLocal),
    was: dep?.scheduledTime || null,
    detail: gateLine(dep),
  };
  const arrival = {
    code: next.destination,
    verb: arr?.verb || 'Arrives',
    time: arr?.time || localClock(next.arrivingAtLocal),
    was: arr?.scheduledTime || null,
    detail: arr?.baggage ? `Bags: carousel ${arr.baggage}` : gateLine(arr),
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className={cn('w-full', className)}
    >
    <Link to={`/trips?tripId=${encodeURIComponent(next.bookingId)}`} className="group relative block w-full overflow-hidden rounded-2xl sm:rounded-3xl text-left">
      <div className={`absolute inset-0 ${TRIPS_LIST_ITEM_SHELL}`} />
      <div className="relative p-4 sm:p-6">
        <div className="flex items-center justify-between gap-3">
          <p className="text-[11px] sm:text-xs uppercase tracking-[0.18em] text-brand-300 font-normal">{eyebrow}</p>
          <div className="flex items-center gap-1 text-white/50">
            {reference && <span className="font-mono text-[11px] sm:text-xs">{reference}</span>}
            <ChevronRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" aria-hidden="true" />
          </div>
        </div>

        <div className="mt-2 flex items-center gap-3">
          {logo && (
            <img src={logo} alt="" className="w-8 h-8 sm:w-9 sm:h-9 object-contain flex-shrink-0" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          )}
          <div className="min-w-0">
            <p className="text-lg sm:text-xl font-medium text-white tracking-tight truncate">
              {getCityName(next.journeyOrigin)} → {getCityName(next.journeyDestination)}
            </p>
            <p className="text-xs sm:text-sm text-white/60 font-normal truncate">
              {[day, flightLabel].filter(Boolean).join(' · ')}
            </p>
          </div>
        </div>

        <div className="mt-5 flex items-start gap-3 sm:gap-5">
          <FlightEnd end={departure} />
          <div className="flex-1 pt-3 sm:pt-4">
            <RouteLine progress={progress} moving={inTheAir || landed} />
          </div>
          <FlightEnd end={arrival} alignRight />
        </div>

        {(headline || countdown) && (
          <div className="mt-5 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
            {headline ? (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <span className={cn('inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1 text-xs font-normal', (STATUS_TONE_CLASSES[headline.tone] || STATUS_TONE_CLASSES.muted).pill)}>
                  <span className={cn('h-1.5 w-1.5 rounded-full', (STATUS_TONE_CLASSES[headline.tone] || STATUS_TONE_CLASSES.muted).dot)} aria-hidden="true" />
                  {headline.label}
                </span>
                {tick && (
                  <span className="text-sm text-white/80 font-normal whitespace-nowrap">
                    {tick.verb} <span className="tabular-nums text-white">{tick.time}</span>
                  </span>
                )}
              </div>
            ) : (
              <span className="text-sm text-white/70 font-normal">Departs {countdown}</span>
            )}
            {headline && live?.checked_at && (
              <span className="text-[11px] sm:text-xs text-white/40 font-normal">{updatedAgo(live.checked_at, nowMs)}</span>
            )}
          </div>
        )}

        {forecast && (
          <div className="mt-3 rounded-xl border border-amber-400/20 bg-amber-400/[0.06] px-3 py-2.5 text-xs sm:text-sm leading-snug">
            <PredictionsTag />
            <span className="text-amber-100">{forecast.label}.</span> <span className="text-white/70">{forecast.text}</span>
          </div>
        )}

        {connection && (
          <div className={cn('mt-3 text-xs sm:text-sm leading-snug', connectionWarning && 'rounded-xl border px-3 py-2.5', connectionWarning && (connection.tone === 'bad' ? 'border-rose-400/25 bg-rose-400/[0.06]' : 'border-amber-400/20 bg-amber-400/[0.06]'))}>
            {connectionWarning && !forecast && <PredictionsTag />}
            <div className="flex items-start gap-2">
              <ArrowRightLeft className={cn('mt-0.5 h-3.5 w-3.5 flex-shrink-0', CONNECTION_ICON_TONES[connection.tone] || 'text-white/45')} aria-hidden="true" />
              <span className="min-w-0">
                <span className="text-white/90">{connection.label}:</span> <span className="text-white/70">{connection.text}</span>
                {connection.next && (
                  <span className="block text-[11px] sm:text-xs text-white/50">
                    Next: {nextLineParts(connection.next).map((part, i) => (
                      <span key={part}>{i > 0 && ' · '}<span className="whitespace-nowrap">{part}</span></span>
                    ))}
                  </span>
                )}
              </span>
            </div>
          </div>
        )}
      </div>
    </Link>
    </motion.div>
  );
}

TripNowCard.propTypes = {
  className: PropTypes.string,
};

const CONNECTION_ICON_TONES = { bad: 'text-rose-300', warn: 'text-amber-300', info: 'text-sky-300' };

/** One end of the flight: the airport, the time to trust, the booked time it replaced, and the gate. */
function FlightEnd({ end, alignRight = false }) {
  return (
    <div className={cn('min-w-0 flex-shrink-0', alignRight && 'text-right')}>
      <p className="text-2xl sm:text-3xl font-medium text-white tracking-tight leading-none">{end.code}</p>
      <p className="mt-1 text-[11px] sm:text-xs text-white/50 font-normal truncate max-w-[9rem] sm:max-w-[12rem]">{getCityName(end.code)}</p>
      {end.time && (
        <p className="mt-3 text-[11px] sm:text-xs text-white/50 font-normal">
          {end.verb}
        </p>
      )}
      {end.time && (
        <p className={cn('mt-0.5 flex flex-wrap items-baseline gap-x-2', alignRight && 'justify-end')}>
          <span className="text-base sm:text-lg text-white font-normal whitespace-nowrap">{end.time}</span>
          {end.was && <span className="text-xs text-white/40 line-through whitespace-nowrap">{end.was}</span>}
        </p>
      )}
      {end.detail && <p className="mt-1 text-[11px] sm:text-xs text-white/60 font-normal whitespace-nowrap">{end.detail}</p>}
    </div>
  );
}

FlightEnd.propTypes = {
  end: PropTypes.shape({
    code: PropTypes.string,
    verb: PropTypes.string,
    time: PropTypes.string,
    was: PropTypes.string,
    detail: PropTypes.string,
  }).isRequired,
  alignRight: PropTypes.bool,
};

/** The line between the airports, with the plane along it once it's flying. */
function RouteLine({ progress, moving }) {
  const at = moving ? progress : 0.5;
  return (
    <div className="relative h-4 w-full" aria-hidden="true">
      <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 border-t border-dashed border-white/20" />
      {moving && <div className="absolute left-0 top-1/2 h-px -translate-y-1/2 bg-brand-300/80" style={{ width: `${at * 100}%` }} />}
      <span className="absolute left-0 top-1/2 h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/60" />
      <span className="absolute right-0 top-1/2 h-1.5 w-1.5 translate-x-1/2 -translate-y-1/2 rounded-full bg-white/60" />
      <Plane className="absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rotate-45 text-white" style={{ left: `${at * 100}%` }} />
    </div>
  );
}

RouteLine.propTypes = {
  progress: PropTypes.number.isRequired,
  moving: PropTypes.bool,
};
